import { defineBlock, EditableText } from "eddev/blocks"
import { ContentItem, ContentItemDetailed, ContentItemSquare } from "@components/site/ContentItem"
import { Button } from "@components/atoms/Button"
import { useEffect, useState } from "react"
import { ContentItemFragment, ContentTypeEnum, OrderEnum } from "../../types.graphql"
import { useSearchParams } from "eddev/routing"
import { useInfiniteContentItems } from "@hooks/queries"
import { Themed } from "@components/atoms/Themed"
import { Select } from "@components/atoms/Select"
import { AnimatePresence, motion } from "motion/react"
import { IconClose } from "@components/atoms/Icon"

export const meta: BlockMeta = {
  title: "All Content Items",
  icon: "block-default",
  tags: ["#page"],
}

type searchTypes = "articles" | "podcasts" | "whitepapers"

export default defineBlock("content/all-content-items", (props) => {
  const [view, setView] = useState<"grid" | "list">("grid")
  const [searchParams, searchFilter] = useSearchParams({
    articles: "true",
    podcasts: "true",
    whitepapers: "true",
    order: "desc",
  })

  const lookup = useInfiniteContentItems(
    {
      order: searchParams.order === "asc" ? OrderEnum.Asc : OrderEnum.Desc,
      types: [ContentTypeEnum.Article, ContentTypeEnum.Podcast, ContentTypeEnum.Whitepaper].filter((type) => {
        if (type === ContentTypeEnum.Article && searchParams.articles !== "false") return true
        if (type === ContentTypeEnum.Podcast && searchParams.podcasts !== "false") return true
        if (type === ContentTypeEnum.Whitepaper && searchParams.whitepapers !== "false") return true
        return false
      }),
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
    },
  )

  let items = lookup.data?.nodes || []

  const hasAll =
    searchParams.articles !== "false" && searchParams.podcasts !== "false" && searchParams.whitepapers !== "false"
  const hasArticles = searchParams.articles !== "false" && !hasAll
  const hasPodcasts = searchParams.podcasts !== "false" && !hasAll
  const hasWhitepapers = searchParams.whitepapers !== "false" && !hasAll

  // filter
  items = items.filter((item) => {
    if (item.__typename === "Article" && searchParams.articles !== "false") return true
    if (item.__typename === "Podcast" && searchParams.podcasts !== "false") return true
    if (item.__typename === "WhitePaper" && searchParams.whitepapers !== "false") return true
  })

  const setAll = () => {
    searchFilter.set("articles", "true")
    searchFilter.set("podcasts", "true")
    searchFilter.set("whitepapers", "true")
  }

  useEffect(() => {
    if (
      searchParams.articles === "false" &&
      searchParams.podcasts === "false" &&
      searchParams.whitepapers === "false"
    ) {
      setAll()
    }
  }, [searchParams.articles, searchParams.podcasts, searchParams.whitepapers])

  return (
    <div className="w-grid mx-auto flex flex-col gap-7 my-8">
      <EditableText as="p" store="title" className="type-label-xl" placeholder="Optional Title" />
      <Themed theme="white" className="w-full flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button
            className="rounded-sm"
            onClick={() =>
              searchParams.order === "asc" ? searchFilter.set("order", "desc") : searchFilter.set("order", "asc")
            }
          >
            {searchParams.order === "desc" ? <span>Arrange: New to old</span> : <span>Arrange: Old to new</span>}
          </Button>

          <Select
            label="Type"
            options={[
              { label: "Articles", value: "articles" },
              { label: "Podcasts", value: "podcasts" },
              { label: "Whitepapers", value: "whitepapers" },
            ]}
            multiple
            value={
              hasAll
                ? ["articles", "podcasts", "whitepapers"]
                : [
                    ...(hasArticles ? ["articles"] : []),
                    ...(hasPodcasts ? ["podcasts"] : []),
                    ...(hasWhitepapers ? ["whitepapers"] : []),
                  ]
            }
            onChange={(v) => {
              if (v.length === 0 || v.length === 5) {
                setAll()
              } else {
                for (const key of ["articles", "podcasts", "whitepapers"]) {
                  searchFilter.set(key as searchTypes, v.includes(key) ? "" : "false")
                }
              }
            }}
          />

          <AnimatePresence>
            {!hasAll && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.1 }}
              >
                <Button variant="inverted" icon={<IconClose />} iconAlign="right" onClick={() => setAll()}>
                  Reset
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <Button className="hidden md:flex rounded-sm" onClick={() => setView(view === "grid" ? "list" : "grid")}>
          {view === "grid" && <span>View: Grid</span>}
          {view === "list" && <span>View: List</span>}
        </Button>
      </Themed>
      <div className="w-full flex flex-col gap-5 min-h-[25vw]">
        {view === "list" && (
          <>
            {items.map(
              (item, index) => !!item && "__typename" in item && <ContentItemDetailed key={index} item={item} />,
            )}
          </>
        )}
        {view === "grid" && (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-5">
              {items.map(
                (item, index) =>
                  !!item &&
                  index % 2 === 0 && (
                    <div key={index} className="w-full">
                      <ContentItemSquare item={item} />
                    </div>
                  ),
              )}
            </div>
            <div className="flex flex-col gap-5">
              {items.map(
                (item, index) =>
                  !!item &&
                  index % 2 === 1 && (
                    <div key={index} className="w-full">
                      <ContentItemSquare item={item} />
                    </div>
                  ),
              )}
            </div>
          </div>
        )}
      </div>

      {lookup.hasNextPage && (
        <div className="flex justify-end">
          <Button className="self-end" data-transition variant="inverted" onClick={() => lookup.fetchNextPage()}>
            View more
          </Button>
        </div>
      )}
    </div>
  )
})
